import React, { useState } from 'react';
import { TextField, Typography, Box, Button, Grid, CircularProgress } from '@mui/material';
import SwitchComponent from '../components/switch';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CreateCustomRole, getUserInfo } from '../services/services';
import { user_information } from '../redux/actions/action';
import { useToast } from '../toast/toastContext';
import { Colors, isLightTheme } from '../config/default';
import { clearSessionPreservingTab } from '../utils/tabSession';

const CreateUserRole = ({ rolePermissions, show, editrole, editRoleName, setOpen, setLoading }) => {
  const navigate = useNavigate();
  // Every permission this screen knows about, and the order the switches are
  // laid out in. The grid below renders exactly these keys, so a permission
  // missing from here has no switch — which is how the manager-flow grant
  // shipped unreachable: this object was the fallback for a NEW role only, and
  // an existing role rendered whatever its stored object happened to contain.
  // Roles created before a permission existed do not carry its key, so the one
  // screen that could grant it never drew it.
  //
  // Merging stored over the defaults keeps that from recurring: a key the role
  // has is shown with its saved value, a key it predates is shown as off. It
  // also stops the save from 400ing on such a role — every key Joi marks
  // required is now always present in the payload, including the two the grid
  // filters out of view.
  const defaultPermissions = {
    updateUsers: false,
    updatecompanyProfileSettings: false,
    updatePolicy: false,
    updateIntergration: false,
    onholdDocument: false,
    rejectDocument: false,
    uploadDocument: false,
    pullBackDocument: false,
    consolidateLineItems: false,
    manualApprovers: false,
    createNewRoles: false,
    updateRoleConfigurations: false,
    // The manager document flow: uploads land in the role's own queue and the
    // role acts as first approver, exactly like the Manager role. The backend
    // keeps it from standing without uploadDocument.
    managerUploadFlow: false
  };
  const initialPermissions = { ...defaultPermissions, ...(rolePermissions || {}) };
  const [roleName, setRoleName] = useState('');
  const [roleError, setRoleError] = useState('');
  const [permissions, setPermissions] = useState(initialPermissions);
  const cognitoUsername = useSelector(
    (state) => state?.signIn?.signIn?.cognitoRes?.accessToken?.payload?.username
  );
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const userDetails = useSelector((state) => state?.userInfo?.userInfo);
  const companyId = userDetails?.company?._id;
  const [isLoading, setIsLoading] = useState(false);
  const userRoleName = editRoleName?.replace(/[^a-zA-Z0-9 ]/g, ' ');

  const handlePermissionChange = (key) => (value) => {
    setPermissions((prev) => {
      const updated = { ...prev, [key]: value };

      if ((key === 'createNewRoles' || key === 'updateRoleConfigurations') && value) {
        updated.updateUsers = true;
      }

      if (key === 'managerUploadFlow' && value) {
        updated.uploadDocument = true;
      }

      if (key === 'uploadDocument' && !value) {
        updated.managerUploadFlow = false;
      }

      if (key === 'updateUsers' && !value) {
        if (prev.createNewRoles) updated.createNewRoles = false;
        if (prev.updateRoleConfigurations) updated.updateRoleConfigurations = false;
      }

      return updated;
    });
  };

  const validateRoleName = () => {
    const trimmedName = roleName.trim();

    if (!/^[a-zA-Z ]+$/.test(trimmedName)) {
      setRoleError(
        'Role name must contain only letters and spaces (no numbers or special characters).'
      );
      return false;
    }

    setRoleError('');
    return true;
  };
  const handleSubmit = async () => {
    setIsLoading(true);

    if (show === 'editUserRole') {
      if (typeof editrole === 'function') {
        await editrole(editRoleName, permissions); // make sure `editrole` returns a promise
        setOpen(false);

        setIsLoading(false);
      }
      return;
    }

    if (!validateRoleName()) return;

    const trimmedName = roleName.trim();

    const response = await CreateCustomRole(companyId, trimmedName, permissions);

    if (response?.status === 200) {
      await userInfo();
      setOpen(false);
      setLoading(true);
      setIsLoading(false);
      showToast(response?.data?.metadata?.message, 'success');
    } else {
      showToast(
        response?.response?.data?.metadata?.message || response?.data?.metadata?.message,
        'error'
      );
    }
    setLoading(false);
  };
  const userInfo = async () => {
    const getUserresult = await getUserInfo(cognitoUsername);
    if (getUserresult?.status === 200) {
      dispatch(user_information(getUserresult?.data?.payload?.data));
    } else if (getUserresult?.response?.status === 401) {
      clearSessionPreservingTab();
      navigate('/');
    }
  };

  return (
    <Box sx={{ maxWidth: 550, margin: 'auto' }}>
      <Typography
        variant="h6"
        sx={{ color: Colors.WHITE, marginBottom: '1em', textAlign: 'center' }}>
        {show === 'Add_User_Role' ? 'Create New Role' : 'Edit User Role'}
      </Typography>

      {show === 'Add_User_Role' && (
        <TextField
          multiline
          fullWidth
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          placeholder={'Role Name'}
          variant="standard"
          error={Boolean(roleError)}
          helperText={roleError}
          InputProps={{
            disableUnderline: true,
            style: {
              color: Colors.WHITE,
              backgroundColor: isLightTheme() ? Colors.BG_BLACK : Colors.NAV_BLACK,
              border: isLightTheme() ? '1px solid rgba(0,0,0,0.2)' : 'none',
              fontSize: '1rem',
              borderRadius: '10px',
              padding: '0.8em'
            }
          }}
        />
      )}
      {show === 'editUserRole' && (
        <Typography variant="h6" sx={{ color: Colors.WHITE, marginBottom: '1em' }}>
          {userRoleName}
        </Typography>
      )}
      <Grid
        container
        spacing={1}
        sx={{
          backgroundColor: isLightTheme() ? Colors.BG_BLACK : Colors.NAV_BLACK,
          border: isLightTheme() ? '1px solid rgba(0,0,0,0.12)' : 'none',
          borderRadius: '10px',
          padding: '1em',
          marginTop: 2,
          width: '100%',
          marginLeft: '0px'
        }}>
        {Object.entries(permissions)
          .filter(([key]) => !['pullBackDocument', 'manualApprovers'].includes(key))
          .map(([key, value], index) => (
            <Grid item xs={12} sm={6} key={key}>
              <SwitchComponent
                description={key
                  .replace(/^updatecompany/, 'updateCompany') // fix specific bad key
                  .replace(/([a-z])([A-Z])/g, '$1 $2')
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/\s+/g, ' ')
                  .trim()
                  .replace(/\b\w/g, (str) => str?.toUpperCase())}
                checked={value}
                setChecked={handlePermissionChange(key)}
              />
            </Grid>
          ))}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleSubmit}
          sx={{ borderRadius: '10px', textTransform: 'none', width: '10em' }}
          disabled={isLoading}>
          {isLoading ? (
            <CircularProgress size={20} color="primary" />
          ) : show === 'editUserRole' ? (
            'Save'
          ) : (
            'Create Role'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateUserRole;
